import { useEffect, useState } from 'react';
import api from '../api';

const statusEmoji = {
  pending: '⏳',
  approved: '✅',
  rejected: '❌',
  withdrawn: '🚫',
  paid: '✅',
  unpaid: '⚠️',
};

const fmtPLN = (v) =>
  v == null ? '—' : new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(v);
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('pl-PL') : '—');

export default function MyDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/dashboard/me')
      .then((res) => { setData(res.data); setLoading(false); })
      .catch((e) => { setError(e.userMessage || e.message || 'Failed'); setLoading(false); });
  }, []);

  if (loading) return <div style={{ padding: 32, textAlign: 'center' }}>⏳ Ładowanie panelu…</div>;
  if (error) return <div style={{ padding: 32, color: '#c00' }}>❌ {error}</div>;
  if (!data) return null;

  const cardStyle = {
    background: 'var(--card-bg, #fff)',
    border: '1px solid var(--border, #e5e7eb)',
    borderRadius: 12,
    padding: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    marginBottom: 16,
  };
  const headerStyle = { fontSize: 16, fontWeight: 600, marginBottom: 12, color: 'var(--text, #111)' };
  const gridStyle = { display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' };

  // ── ZUS widget
  const zus = data.zus_status || {};
  const zusPaid = zus.paid;
  const zusColor = zusPaid ? '#10b981' : '#f59e0b';

  // ── Tax YTD
  const tax = data.tax_summary || {};

  // ── Karta pobytu
  const kartas = data.karta_pobytu || [];

  // ── Deadlines
  const deadlines = data.upcoming_deadlines || [];

  // ── Recent docs
  const recent = data.recent_documents || [];

  // ── Active reminders
  const reminders = data.active_reminders || [];

  return (
    <div style={{ padding: 16, maxWidth: 1200, margin: '0 auto' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 16 }}>📊 Мой кабинет</h1>

      <div style={gridStyle}>
        {/* ── Upcoming deadlines */}
        <div style={cardStyle}>
          <div style={headerStyle}>📅 Сроки на 30 дней ({deadlines.length})</div>
          {deadlines.length === 0 ? (
            <div style={{ color: '#6b7280' }}>Нет сроков в ближайшие 30 дней</div>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {deadlines.slice(0, 8).map((d, i) => {
                const days = d.days_until;
                const urgent = days != null && days <= 3;
                return (
                  <li key={i} style={{
                    padding: '8px 0',
                    borderBottom: '1px solid var(--border-light, #f3f4f6)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <div>
                      <div style={{ fontWeight: 500 }}>{d.label_ru || d.label_pl || d.label_en || d.code}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{fmtDate(d.due_date)}</div>
                    </div>
                    <div style={{
                      padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                      background: urgent ? '#fee2e2' : '#dbeafe',
                      color: urgent ? '#dc2626' : '#1e40af',
                    }}>
                      {days != null ? `${days} дн.` : '—'}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* ── ZUS status */}
        <div style={cardStyle}>
          <div style={headerStyle}>🏛 ZUS — текущий месяц</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: zusColor }}>
            {zusPaid ? '✅ Оплачено' : '⚠️ Не оплачено'}
          </div>
          <div style={{ marginTop: 8, color: '#6b7280' }}>
            {String(zus.month || '—').padStart(2, '0')}.{zus.year || '—'} ·
            Сумма: <b>{fmtPLN(zus.amount_pln)}</b>
          </div>
          {!zusPaid && (
            <div style={{ marginTop: 12, padding: 8, background: '#fef3c7', borderRadius: 6, fontSize: 13 }}>
              ZUS платится до 20-го следующего месяца. Не забудь!
            </div>
          )}
        </div>

        {/* ── Tax YTD */}
        <div style={cardStyle}>
          <div style={headerStyle}>💰 Налоги — {tax.year || '—'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>YTD доход</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{fmtPLN(tax.income_pln)}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>YTD налог уплачен</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{fmtPLN(tax.tax_paid_pln)}</div>
            </div>
            <div style={{ gridColumn: 'span 2', borderTop: '1px solid #f3f4f6', paddingTop: 8 }}>
              <div style={{ fontSize: 12, color: '#6b7280' }}>Оценочно осталось доплатить</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#dc2626' }}>{fmtPLN(tax.estimated_remaining_pln)}</div>
            </div>
          </div>
        </div>

        {/* ── Karta pobytu */}
        {kartas.length > 0 && (
          <div style={cardStyle}>
            <div style={headerStyle}>🏠 Karta pobytu</div>
            {kartas.map((k) => (
              <div key={k.id} style={{ padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ fontWeight: 600 }}>
                  {statusEmoji[k.status] || '⏳'} #{k.id} · {k.permit_type}
                </div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>{k.voivodeship}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  Подано: {fmtDate(k.application_date)} ·
                  Ожидается: {fmtDate(k.expected_decision_date)}
                  {k.days_until_expected != null ? ` (${k.days_until_expected} дн.)` : ''}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Recent documents */}
        <div style={cardStyle}>
          <div style={headerStyle}>📄 Последние документы</div>
          {recent.length === 0 ? (
            <div style={{ color: '#6b7280' }}>Документов пока нет</div>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {recent.slice(0, 6).map((doc) => (
                <li key={doc.id} style={{ padding: '6px 0', borderBottom: '1px solid #f3f4f6',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 500 }}>📑 {doc.number}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{fmtDate(doc.issue_date)} · {doc.status}</div>
                  </div>
                  <div style={{ fontWeight: 600 }}>{fmtPLN(doc.total_pln)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── Active reminders */}
        <div style={cardStyle}>
          <div style={headerStyle}>🔔 Активные напоминания ({reminders.length})</div>
          {reminders.length === 0 ? (
            <div style={{ color: '#6b7280' }}>
              Напоминаний нет. Добавь через бота: <code>/reminders</code>
            </div>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {reminders.slice(0, 6).map((r) => (
                <li key={r.id} style={{ padding: '6px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ fontWeight: 500 }}>🔔 {r.title}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    {fmtDate(r.due_date)} · уведомить за {r.notify_days_before} дн.
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div style={{ marginTop: 24, padding: 16, background: '#f3f4f6', borderRadius: 8, fontSize: 12, color: '#6b7280' }}>
        Обновлено: {data.generated_at ? new Date(data.generated_at).toLocaleString('pl-PL') : '—'}
      </div>
    </div>
  );
}
